import { Clock, Loader2, CheckCircle, CheckCircle2, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ImageFile } from '../App';

interface SidebarItemProps {
    image: ImageFile;
    isActive?: boolean;
    onClick?: () => void;
    actions?: React.ReactNode;
    variant: 'queue' | 'approved';
}

export function SidebarItem({ image, isActive, onClick, actions, variant }: SidebarItemProps) {
    const { t } = useTranslation();
    const isApproved = variant === 'approved';

    return (
        <div
            onClick={onClick}
            className={`group relative h-16 border rounded-xl overflow-hidden flex items-center gap-3 p-2 transition-all 
        ${isActive ? 'border-primary bg-primary/5 ring-1 ring-primary shadow-sm' : 'hover:bg-muted/50 border-border/50'}
        ${isApproved ? 'bg-white shadow-sm' : ''}
        ${onClick ? 'cursor-pointer' : ''}
      `}
        >
            <div
                className={`h-full aspect-square rounded-lg overflow-hidden shrink-0 
                    ${isApproved ? 'bg-checkered border' : 'bg-muted/20'}
                `}
                style={isApproved ? { backgroundSize: '8px 8px' } : {}}
            >
                <img
                    src={isApproved ? image.processedUrl : image.originalUrl}
                    className={`h-full w-full ${isApproved ? 'object-contain p-1 drop-shadow-sm' : 'object-cover'}`}
                    alt="Thumbnail"
                />
            </div>

            <div className="flex-1 min-w-0 pr-10">
                <p className="text-[9px] font-black truncate uppercase tracking-tight opacity-70">{image.file.name}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                    {!isApproved && image.status === 'pending' && <Clock className="h-2.5 w-2.5 text-muted-foreground/50" />}
                    {!isApproved && image.status === 'processing' && <Loader2 className="h-2.5 w-2.5 animate-spin text-primary" />}
                    {!isApproved && image.status === 'done' && <CheckCircle className="h-2.5 w-2.5 text-green-500" />}
                    {isApproved && <CheckCircle2 className="h-2.5 w-2.5 text-primary" />}

                    <span className={`text-[8px] font-black uppercase tracking-widest 
            ${image.status === 'processing' ? 'text-primary' : 'text-muted-foreground/60'}
            ${isApproved ? 'text-primary' : ''}
          `}>
                        {isApproved ? t('status.approved_artifact') : t(`status.${image.status}`)}
                    </span>
                </div>
            </div>

            <div className="absolute right-2 transition-all">
                {actions ? (
                    <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                        {actions}
                    </div>
                ) : (
                    <>
                        {image.status === 'done' && !isActive && (
                            <div className="opacity-50 group-hover:opacity-100">
                                <ChevronRight className="h-4 w-4 text-primary" />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
